import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../contexts/LanguageContext';

// Hook pour animer au scroll (Intersection Observer)
function useReveal(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setVisible(true); obs.unobserve(el); }
    }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

export default function Home() {
  const { t } = useLang();
  const [introPhase, setIntroPhase] = useState('logo');

  // Scroll-driven hero + transition
  const heroRef = useRef(null);
  const [sp, setSp] = useState(0);

  const handleScroll = useCallback(() => {
    if (!heroRef.current) return;
    const rect = heroRef.current.getBoundingClientRect();
    const total = heroRef.current.offsetHeight - window.innerHeight;
    const progress = Math.max(0, Math.min(1, -rect.top / total));
    setSp(progress);
  }, []);

  useEffect(() => {
    const t1 = setTimeout(() => setIntroPhase('hold'), 1500);
    const t2 = setTimeout(() => setIntroPhase('split'), 3200);
    const t3 = setTimeout(() => setIntroPhase('done'), 4200);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      clearTimeout(t1); clearTimeout(t2); clearTimeout(t3);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  // ── SCROLL PHASES ──
  const heroTextOpacity = sp < 0.1 ? 1 : Math.max(0, 1 - (sp - 0.1) / 0.15);
  const circleSize = sp < 0.25 ? 0 : Math.min(((sp - 0.25) / 0.3) * 150, 150);
  const heroImgOpacity = sp < 0.25 ? 1 : Math.max(0, 1 - (sp - 0.25) / 0.25);
  const collierScale = 1 + Math.max(0, sp - 0.25) * 0.1;

  const t1Opacity = (() => {
    if (sp < 0.50) return 0;
    if (sp < 0.60) return (sp - 0.50) / 0.10;
    return 1;
  })();

  const t1Y = (1 - t1Opacity) * 25;

  return (
    <div>
      {/* ── INTRO / SPLASH SCREEN — LOGO ── */}
      {introPhase !== 'done' && (
        <div className="fixed inset-0 z-[100] pointer-events-none">
          <div className={`absolute top-0 left-0 right-0 h-1/2 ${introPhase === 'split' ? 'split-top' : ''}`} style={{ backgroundColor: '#2a080d' }} />
          <div className={`absolute bottom-0 left-0 right-0 h-1/2 ${introPhase === 'split' ? 'split-bottom' : ''}`} style={{ backgroundColor: '#2a080d' }} />
          <div className={`absolute inset-0 flex flex-col items-center justify-center ${introPhase === 'split' ? 'splash-overlay' : ''}`}
               style={introPhase === 'split' ? { animationDelay: '0.3s' } : {}}>
            {/* Logo complet */}
            <img src="/photos/Pernety_logo_fd_transparent.png" alt="Pernety"
              className="splash-logo h-72 md:h-96 lg:h-[32rem] w-auto"
              style={{ filter: 'invert(1) brightness(2)' }} />
          </div>
        </div>
      )}

      {/* ── HERO + TRANSITION + STORYTELLING ── */}
      <section ref={heroRef} className="relative bg-black" style={{ height: '350vh' }}>
        <div className="sticky top-0 h-screen overflow-hidden">

          {/* Layer 1 : Bracelet (en dessous) */}
          <div className="absolute inset-0 pointer-events-none" style={{
            clipPath: `circle(${circleSize}% at 50% 50%)`
          }}>
            <img src="/photos/IMG_0973.jpeg" alt="Bijou Pernety"
              className="w-full h-full object-cover"
              style={{
                filter: 'brightness(0.7) contrast(1.1) saturate(0.85)',
                transform: `scale(${collierScale})`
              }} />
            <div className="absolute inset-0 bg-black/35" />
            <div className="absolute inset-0" style={{ boxShadow: 'inset 0 0 200px 100px rgba(0,0,0,0.4)' }} />
          </div>

          {/* Layer 2 : Bague hero (au-dessus) */}
          <div className="absolute inset-0 pointer-events-none" style={{ opacity: heroImgOpacity }}>
            <img src="/photos_bijoux/IMG_2707.jpeg" alt="Bague opale"
              className="w-full h-full object-cover object-center md:object-[70%_center]"
              style={{ filter: 'brightness(0.9) contrast(1.05) saturate(0.9)' }} />
            <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/20 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-black/10" />
            <div className="absolute inset-0" style={{ boxShadow: 'inset 0 0 150px 60px rgba(0,0,0,0.2)' }} />
          </div>

          {/* Anneau blanc subtil autour du cercle */}
          {circleSize > 2 && circleSize < 140 && (
            <div className="absolute inset-0 pointer-events-none" style={{
              background: `radial-gradient(circle at 50% 50%, transparent ${circleSize - 0.4}%, rgba(255,255,255,0.08) ${circleSize}%, transparent ${circleSize + 0.6}%)`
            }} />
          )}

          {/* Particules */}
          <div className="absolute top-1/4 right-1/3 w-1 h-1 rounded-full animate-shimmer bg-white/10" />
          <div className="absolute top-1/3 right-1/4 w-0.5 h-0.5 bg-white/15 rounded-full animate-shimmer delay-800" />

          {/* ── HERO TEXT ── */}
          <div className="absolute inset-0 flex items-center z-20"
            style={{ opacity: heroTextOpacity, transform: `translateY(${(1 - heroTextOpacity) * -30}px)` }}>
            <div className="px-8 md:px-16 lg:px-24 max-w-3xl">
              <h1 className="text-5xl md:text-6xl lg:text-7xl tracking-[0.25em] text-white mb-4 font-light leading-none" style={{ fontFamily: "'Armoria', serif" }}>
                <span style={{ display: 'inline-block', transform: 'scaleY(1.3)', transformOrigin: 'center' }}>
                  PERNETY
                </span>
              </h1>
              <div className="h-px mb-6 bg-white/20" style={{ width: '80px' }} />
              <p className="text-lg md:text-2xl lg:text-3xl font-light tracking-[0.15em] text-white/50 italic mb-12" style={{ fontFamily: "'Playfair Display', serif" }}>
                {t('home.heroTagline')}
              </p>
              <p className="text-white/80 text-base md:text-lg leading-relaxed max-w-md font-light">
                {t('home.heroDescription')}
              </p>
            </div>
          </div>

          {/* ── TEXTE 1 — Phase 2 ── */}
          <div className="absolute inset-0 flex items-start justify-center px-6 pt-48 md:pt-64 z-10"
            style={{ opacity: t1Opacity, transform: `translateY(${t1Y}px)` }}>
            <div className="max-w-5xl text-center">
              <h2 className="leading-tight font-light tracking-wide text-white" style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 'clamp(32px, 5vw, 64px)'
              }}>
                {t('home.phase2')}
              </h2>
            </div>
          </div>

        </div>
      </section>

      <BottomSections />
    </div>
  );
}

// ── SECTIONS DU BAS ──
function BottomSections() {
  const { t } = useLang();
  const [sylRef, sylVis] = useReveal(0.15);
  const [sylTxtRef, sylTxtVis] = useReveal(0.2);
  const [sfRef, sfVis] = useReveal(0.15);
  const [sfTxtRef, sfTxtVis] = useReveal(0.2);
  const [ctaRef, ctaVis] = useReveal(0.2);

  return (
    <>
      {/* ── SYLVIE — LA CREATRICE ── */}
      <section className="relative py-0 bg-white overflow-hidden">
        <div className="w-full grid grid-cols-1 md:grid-cols-12 min-h-[90vh]">
          <div ref={sylRef}
            className={`md:col-span-6 relative overflow-hidden transition-all duration-[1.5s] ${sylVis ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}>
            <img src="/photos/IMG_0970.jpeg" alt="Sylvie — Fondatrice"
              className="w-full h-[500px] md:h-full object-cover" />
          </div>

          <div ref={sylTxtRef}
            className={`md:col-span-6 relative flex flex-col justify-center py-20 md:py-0 transition-all duration-[1.5s] delay-300 ${sylTxtVis ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}>
            <p className="leading-relaxed mb-32 px-8 md:px-16 lg:px-20" style={{
              fontFamily: "'Playfair Display', serif",
              color: '#7a7a7a',
              fontSize: 'clamp(16px, 1.3vw, 19px)'
            }}>
              {t('home.sylvieIntroLine1')}<br />
              {t('home.sylvieIntroLine2')}
            </p>

            <h2 className="text-black leading-tight font-normal text-center md:whitespace-nowrap px-4" style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: 'clamp(24px, 3.5vw, 50px)'
            }}>
              {t('home.sylvieTitle')}
            </h2>
          </div>
        </div>
      </section>

      {/* ── SAVOIR-FAIRE ── */}
      <section className="relative py-0 bg-black overflow-hidden">
        <div className="w-full grid grid-cols-1 md:grid-cols-12 min-h-[90vh]">
          <div ref={sfTxtRef}
            className={`md:col-span-6 flex items-center justify-center px-8 py-20 md:py-0 order-2 md:order-1 transition-all duration-[1.5s] delay-300 ${sfTxtVis ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}>
            <div className="text-center text-white italic font-light leading-tight" style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: 'clamp(24px, 2.8vw, 40px)'
            }}>
              <p>{t('home.savoirFaireLine1')}</p>
              <p>{t('home.savoirFaireLine2')}</p>
            </div>
          </div>

          <div ref={sfRef}
            className={`md:col-span-6 relative overflow-hidden order-1 md:order-2 transition-all duration-[1.5s] ${sfVis ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}>
            <img src="/photos/IMG_2711.jpeg" alt="Savoir-faire Pernety"
              className="w-full h-[500px] md:h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-l from-transparent to-black/30" />
          </div>
        </div>
      </section>

      {/* ── CTA CONTACT / RDV ── */}
      <section ref={ctaRef} className="relative py-20 md:py-24 px-6 bg-white overflow-hidden">
        <div className={`max-w-5xl mx-auto flex flex-col md:flex-row md:items-center md:justify-between gap-10 transition-all duration-[1.5s] ${ctaVis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div style={{ color: '#2B2B2B', fontFamily: "'Inter', sans-serif" }}>
            <p className="text-base md:text-lg mb-2 leading-relaxed">
              {t('home.ctaText1')}
            </p>
            <p className="text-base md:text-lg leading-relaxed">
              {t('home.ctaText2')}
            </p>
          </div>

          <Link to="/collection"
            className="inline-block px-10 py-4 border border-[#2B2B2B] text-[#2B2B2B] text-[11px] tracking-[0.25em] uppercase transition-all duration-500 hover:bg-[#2B2B2B] hover:text-white whitespace-nowrap"
            style={{ fontFamily: "'Inter', sans-serif" }}>
            {t('home.ctaButton')}
          </Link>
        </div>
      </section>
    </>
  );
}
